import { useLingui } from "@lingui/react/macro";
import {
  Baby,
  Brain,
  CloudDownload,
  Cpu,
  CreditCard,
  Gauge,
  KeyRound,
  LifeBuoy,
  MessageCircle,
  Monitor,
  Settings,
  ShieldCheck,
  Volume2,
  Wallet,
} from "lucide-react";
import type { ComponentType } from "react";
import { useHostedService } from "../lib/hosted";
import type { SettingsSection } from "./shell-overlay";

export type SettingsNavItem = {
  id: SettingsSection;
  group: "account" | "agent" | "help";
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
};

/** The settings sections this deployment offers, in nav order; Space opens from its own menu. */
export function useSettingsNavItems(available: {
  messaging: boolean;
  wallet: boolean;
  computer: boolean;
  billing: boolean;
  help: boolean;
  updates: boolean;
  kids: boolean;
}): SettingsNavItem[] {
  const { t } = useLingui();
  const hosted = useHostedService();
  return [
    { id: "general", group: "account", label: t`General`, icon: Settings },
    { id: "usage", group: "account", label: t`Usage`, icon: Gauge },
    ...(available.billing
      ? [{ id: "billing" as const, group: "account" as const, label: t`Billing`, icon: CreditCard }]
      : []),
    ...(available.wallet
      ? [{ id: "wallet" as const, group: "account" as const, label: t`Wallet`, icon: Wallet }]
      : []),
    ...(available.kids
      ? [{ id: "kids" as const, group: "account" as const, label: t`Kids`, icon: Baby }]
      : []),
    ...(hosted
      ? []
      : [{ id: "models" as const, group: "agent" as const, label: t`Models`, icon: Cpu }]),
    ...(hosted
      ? []
      : [{ id: "voice" as const, group: "agent" as const, label: t`Voice`, icon: Volume2 }]),
    { id: "confirmations", group: "agent", label: t`Approvals`, icon: ShieldCheck },
    { id: "vault", group: "agent", label: t`Vault`, icon: KeyRound },
    { id: "memory", group: "agent", label: t`Memory`, icon: Brain },
    ...(available.computer
      ? [{ id: "computer" as const, group: "agent" as const, label: t`Computer`, icon: Monitor }]
      : []),
    ...(available.messaging
      ? [
          {
            id: "messaging" as const,
            group: "agent" as const,
            label: t`Messaging`,
            icon: MessageCircle,
          },
        ]
      : []),
    ...(available.help
      ? [{ id: "help" as const, group: "help" as const, label: t`Help`, icon: LifeBuoy }]
      : []),
    ...(available.updates
      ? [
          {
            id: "updates" as const,
            group: "help" as const,
            label: t`Updates`,
            icon: CloudDownload,
          },
        ]
      : []),
  ];
}
